import React, { createContext, useMemo, useContext } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  fetchDefaultCurrency,
  fetchCodeList,
  fetchExpenseClaimDetails,
  cancelExpenseClaim,
  uploadAttachment,
  fetchAttachments,
  submitExpenseClaim,
  validateExpenseClaim,
  printExpenseClaim,
  deleteExpenseClaimAttachmentList,
  printExpenseClaimAttachment,
  deleteExpenseClaimAttachment,
  saveProcessExpenseClaim,
  acceptProcessExpenseClaim,
  submitProcessExpenseClaim,
  rejectProcessExpenseClaim,
  saveARExpenseClaim,
  fetchExpenseClaimDetailsListWithQuery,
  fetchReimburseRate,
} from '../../../store/apis/expenseService';
import { downloadBlobType } from '../../../store/apis/commonService';

const ExpenseClaimUtilContext = createContext();

export function useExpenseClaimUtilCtx() {
  const ctx = useContext(ExpenseClaimUtilContext);
  if (!ctx) {
    throw new Error(
      'useExpenseClaimutilCtx must be used within ExpenseClaimProvider',
    );
  }
  return ctx;
}

export function ExpenseClaimUtilProvider({ children }) {
  // API calls ==============================================
  const { data: defaultCurrency } = useQuery({
    queryKey: ['defaultCurrency'],
    queryFn: () => fetchDefaultCurrency({ defaultType: 'INFO' }),
  });

  const getExpenseClaimDetailsMutation = useMutation({
    mutationFn: payload => fetchExpenseClaimDetails(payload),
  });

  const { data: expenseCategories } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: () => fetchCodeList({ codeType: 'EXPENSE_CATEGORY' }),
  });

  const fetchDefaultExpenseClaimData = useMutation({
    mutationFn: payload => fetchDefaultCurrency(payload),
  });

  const cancelExpenseClaimMutation = useMutation({
    mutationFn: payload => cancelExpenseClaim(payload),
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: payload => uploadAttachment(payload),
  });

  const fetchAttachmentsMutation = useMutation({
    mutationFn: payload => fetchAttachments(payload),
  });

  const downloadAttachmentMutation = useMutation({
    mutationFn: payload => downloadBlobType(payload),
  });

  const submitExpenseClaimMutation = useMutation({
    mutationFn: payload => submitExpenseClaim(payload),
  });

  const validateExpenseClaimMutation = useMutation({
    mutationFn: payload => validateExpenseClaim(payload),
  });

  const printExpenseClaimMutation = useMutation({
    mutationFn: payload => printExpenseClaim(payload),
  });

  const deleteExpenseClaimAttachmentListMutation = useMutation({
    mutationFn: payload => deleteExpenseClaimAttachmentList(payload),
  });

  const printExpenseClaimAttachmentMutation = useMutation({
    mutationFn: payload => printExpenseClaimAttachment(payload),
  });

  const deleteExpenseClaimAttachmentMutation = useMutation({
    mutationFn: payload => deleteExpenseClaimAttachment(payload),
  });

  const expenseClaimApprovalDetailsMutation = useMutation({
    mutationFn: payload => fetchExpenseClaimDetails(payload),
  });

  const saveARExpenseClaimMutation = useMutation({
    mutationFn: payload => saveARExpenseClaim(payload),
  });

  const reimbursateRateMutation = useMutation({
    mutationFn: payload => fetchReimburseRate(payload),
  });

  // HR Related API calls
  // save expense claim
  const saveProcessExpenseClaimMutation = useMutation({
    mutationFn: payload => saveProcessExpenseClaim(payload),
  });

  const acceptProcessExpenseClaimMutation = useMutation({
    mutationFn: payload => acceptProcessExpenseClaim(payload),
  });

  const submitProcessExpenseClaimMutation = useMutation({
    mutationFn: payload => submitProcessExpenseClaim(payload),
  });

  const rejectProcessExpenseClaimMutation = useMutation({
    mutationFn: payload => rejectProcessExpenseClaim(payload),
  });

  const fetchExpenseClaimDetailsListWithQueryMutation = useMutation({
    mutationFn: payload => fetchExpenseClaimDetailsListWithQuery(payload),
  });

  const fetchCodeListMutation = useMutation({
    mutationFn: payload => fetchCodeList(payload),
  });

  // =========================================================

  const defaultCurrCode = defaultCurrency?.[0]?.CurrCd?.trim() || '';

  const value = useMemo(
    () => ({
      defaultCurrency,
      expenseCategories,
      defaultCurrCode,
      getExpenseClaimDetailsMutation,
      cancelExpenseClaimMutation,
      uploadAttachmentMutation,
      fetchAttachmentsMutation,
      downloadAttachmentMutation,
      submitExpenseClaimMutation,
      validateExpenseClaimMutation,
      printExpenseClaimMutation,
      deleteExpenseClaimAttachmentListMutation,
      printExpenseClaimAttachmentMutation,
      deleteExpenseClaimAttachmentMutation,
      expenseClaimApprovalDetailsMutation,
      saveProcessExpenseClaimMutation,
      acceptProcessExpenseClaimMutation,
      submitProcessExpenseClaimMutation,
      rejectProcessExpenseClaimMutation,
      saveARExpenseClaimMutation,
      fetchExpenseClaimDetailsListWithQueryMutation,
      reimbursateRateMutation,
      fetchCodeListMutation,
      fetchDefaultExpenseClaimData,
    }),
    [
      defaultCurrency,
      expenseCategories,
      defaultCurrCode,
      getExpenseClaimDetailsMutation,
      cancelExpenseClaimMutation,
      uploadAttachmentMutation,
      fetchAttachmentsMutation,
      downloadAttachmentMutation,
      submitExpenseClaimMutation,
      validateExpenseClaimMutation,
      printExpenseClaimMutation,
      deleteExpenseClaimAttachmentListMutation,
      printExpenseClaimAttachmentMutation,
      deleteExpenseClaimAttachmentMutation,
      expenseClaimApprovalDetailsMutation,
      saveProcessExpenseClaimMutation,
      acceptProcessExpenseClaimMutation,
      submitProcessExpenseClaimMutation,
      rejectProcessExpenseClaimMutation,
      saveARExpenseClaimMutation,
      fetchExpenseClaimDetailsListWithQueryMutation,
      reimbursateRateMutation,
      fetchCodeListMutation,
      fetchDefaultExpenseClaimData,
    ],
  );

  if (!defaultCurrency) {
    return null;
  }

  return (
    <ExpenseClaimUtilContext.Provider value={value}>
      {children}
    </ExpenseClaimUtilContext.Provider>
  );
}
